'use client';

/**
 * @brisa/ui · facelift/club-map/ClubMapFacelift
 *
 * Feature: migracao-facelift-v1
 * Versão facelift do ClubMap usada em /clubes (toggle Lista/Mapa) — substitui
 * o legacy `club-map.tsx` que ficou amarrado a tokens antigos.
 *
 * Diferenças vs legacy ClubMap:
 *   - Markers replicam o markup do PinMap (3 spans concêntricos com tokens
 *     facelift: accent-yellow p/ partner, text-32 p/ listed, white middle,
 *     text-100 inner dot).
 *   - Estado `selected` adiciona ring-2 outline (anel duplo) ao marker.
 *   - Container respeita altura do parent (não fixa 70vh) — facilita layouts
 *     sticky / split-pane.
 *   - Sem mini-card embed — orquestrador externo decide overlay/sidebar.
 *   - Ref imperativo expõe `fitBounds(clubSlugs)` pra zoom-out programático.
 *
 * Carregamento lazy do mapbox-gl mantido (D9 do ADR — não bloqueia LCP).
 */
import * as React from 'react';

export interface ClubMapFaceliftItem {
  slug: string;
  name: string;
  integration_status: 'listed' | 'partner';
  location_lat: number;
  location_lng: number;
}

export interface ClubMapFaceliftHandle {
  /** Centraliza o mapa pra englobar todos os clubs informados (zoom-out). */
  fitBoundsToClubs: (clubs: ClubMapFaceliftItem[]) => void;
}

export interface ClubMapFaceliftProps {
  clubs: ClubMapFaceliftItem[];
  locale: 'pt' | 'es';
  mapboxToken: string | undefined;
  /** Slug do club destacado (data-state="selected"). */
  selectedClubSlug?: string | null;
  onMarkerClick?: (slug: string, integrationStatus: 'listed' | 'partner') => void;
  onMapLoaded?: (markersCount: number, tokenActive: boolean) => void;
  /** Base href dos popups clicáveis. Default `/clubes`. */
  clubHrefBase?: string;
  className?: string;
}

const ROOT_TOKEN_STYLE = `
.brisa-pinmap-marker { position: relative; display: inline-flex; align-items: center; justify-content: center; width: 24px; height: 24px; background: transparent; border: 0; padding: 0; cursor: pointer; border-radius: 9999px; transition: transform 200ms ease-out; will-change: transform; }
.brisa-pinmap-marker:hover { transform: scale(1.10); }
.brisa-pinmap-marker[data-state="selected"] { box-shadow: 0 0 0 2px var(--marker-ring-color), 0 0 0 3px hsl(0, 0%, 100%); }
.brisa-pinmap-marker .ring-outer { position: absolute; inset: 0; border-radius: 9999px; }
.brisa-pinmap-marker .ring-middle { position: absolute; width: 20px; height: 20px; border-radius: 9999px; background: hsl(var(--neutral-white)); }
.brisa-pinmap-marker .dot-inner { position: absolute; width: 12px; height: 12px; border-radius: 9999px; background: hsl(var(--text-100)); }
.brisa-pinmap-marker[data-integration-status="partner"] { --marker-ring-color: hsl(var(--accent-yellow)); }
.brisa-pinmap-marker[data-integration-status="partner"] .ring-outer { background: hsl(var(--accent-yellow)); }
.brisa-pinmap-marker[data-integration-status="listed"] { --marker-ring-color: hsl(var(--text-32)); }
.brisa-pinmap-marker[data-integration-status="listed"] .ring-outer { background: hsl(var(--text-32)); }

/* Popup do tooltip — mesma estética do Tooltip primitivo (shadcn): bg text-100, white text */
.mapboxgl-popup.brisa-pinmap-popup .mapboxgl-popup-content {
  background: hsl(var(--text-100));
  color: hsl(var(--neutral-white));
  border-radius: 8px;
  padding: 8px 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}
.brisa-pinmap-popup .mapboxgl-popup-tip { border-top-color: hsl(var(--text-100)) !important; border-bottom-color: hsl(var(--text-100)) !important; }
.brisa-pinmap-popup-link {
  color: inherit; text-decoration: none;
  font-family: Archivo, sans-serif; font-size: 13px; font-weight: 500;
  line-height: 1.2; white-space: nowrap; display: inline-block;
}
.brisa-pinmap-popup-link:hover { text-decoration: underline; }
.brisa-pinmap-popup-link:focus-visible { outline: 2px solid hsl(var(--accent-yellow)); outline-offset: 2px; border-radius: 2px; }
`;

function escapeHTML(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export const ClubMapFacelift = React.forwardRef<ClubMapFaceliftHandle, ClubMapFaceliftProps>(
  function ClubMapFacelift(
    {
      clubs,
      locale,
      mapboxToken,
      selectedClubSlug,
      onMarkerClick,
      onMapLoaded,
      clubHrefBase = '/clubes',
      className,
    },
    ref,
  ) {
    const containerRef = React.useRef<HTMLDivElement>(null);
    // mapbox-gl types não expõem default — usamos `unknown` aqui e fazemos
    // narrow nos call sites via cast pra interface mínima que consumimos.
    const mapRef = React.useRef<unknown>(null);
    const markersRef = React.useRef<Map<string, { element: HTMLElement; marker: unknown }>>(
      new Map(),
    );
    const popupRef = React.useRef<{ remove: () => void } | null>(null);
    const mapboxModRef = React.useRef<unknown>(null);
    const [error, setError] = React.useState<string | null>(null);
    const [isLoaded, setIsLoaded] = React.useState(false);

    // Inject CSS uma única vez por mount (sem dependency em tailwind class purge)
    React.useEffect(() => {
      const styleId = 'brisa-pinmap-marker-styles';
      if (document.getElementById(styleId)) return;
      const styleEl = document.createElement('style');
      styleEl.id = styleId;
      styleEl.textContent = ROOT_TOKEN_STYLE;
      document.head.appendChild(styleEl);
    }, []);

    // Init Mapbox uma vez
    React.useEffect(() => {
      if (!mapboxToken) {
        setError('no_token');
        onMapLoaded?.(0, false);
        return;
      }
      let cancelled = false;

      const schedule = (cb: () => void): void => {
        if (typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(cb, { timeout: 2000 });
        } else {
          window.setTimeout(cb, 1);
        }
      };

      schedule(async () => {
        try {
          const mod = (await import('mapbox-gl')) as unknown as {
            default: {
              accessToken: string;
              Map: new (opts: unknown) => {
                on: (evt: string, cb: () => void) => void;
                remove: () => void;
                fitBounds: (b: unknown, opts: unknown) => void;
              };
              Marker: new (opts: { element: HTMLElement }) => {
                setLngLat: (c: [number, number]) => {
                  addTo: (m: unknown) => { remove: () => void };
                };
              };
              LngLatBounds: new () => { extend: (c: [number, number]) => void };
            };
          };
          // @ts-expect-error: mapbox-gl CSS import
          await import('mapbox-gl/dist/mapbox-gl.css');
          if (cancelled || !containerRef.current || mapRef.current) return;

          const mapboxgl = mod.default;
          mapboxgl.accessToken = mapboxToken;
          mapboxModRef.current = mapboxgl;

          const map = new mapboxgl.Map({
            container: containerRef.current,
            style: 'mapbox://styles/mapbox/light-v11',
            center: [2.1734, 41.3851],
            zoom: 12.5,
          });

          map.on('load', () => {
            if (cancelled) return;
            setIsLoaded(true);
            onMapLoaded?.(clubs.length, true);
          });

          mapRef.current = map;
        } catch {
          if (!cancelled) {
            setError('map_load_failed');
            onMapLoaded?.(0, false);
          }
        }
      });

      return () => {
        cancelled = true;
        if (popupRef.current) {
          popupRef.current.remove();
          popupRef.current = null;
        }
        markersRef.current.forEach(({ marker }) => {
          (marker as { remove: () => void }).remove();
        });
        markersRef.current.clear();
        if (mapRef.current) {
          (mapRef.current as { remove: () => void }).remove();
          mapRef.current = null;
        }
      };
    }, [mapboxToken]);

    // Sync markers — diff add/remove conforme clubs[] muda
    React.useEffect(() => {
      const map = mapRef.current as { /* opaque Map handle */ } | null;
      const mapboxgl = mapboxModRef.current as {
        Marker: new (opts: { element: HTMLElement }) => {
          setLngLat: (c: [number, number]) => { addTo: (m: unknown) => { remove: () => void } };
        };
      } | null;
      if (!map || !mapboxgl || !isLoaded) return;

      const next = new Set(clubs.map((c) => c.slug));

      // Remove markers que sumiram
      markersRef.current.forEach(({ marker }, slug) => {
        if (!next.has(slug)) {
          (marker as { remove: () => void }).remove();
          markersRef.current.delete(slug);
        }
      });

      // Helper — abre Popup do Mapbox no club selecionado.
      // Popup nativo do Mapbox segue o pin no zoom/pan automaticamente e tem
      // `<a>` clicável que navega pra página de detalhe.
      const PopupCtor = (mapboxgl as unknown as {
        Popup: new (opts: {
          offset?: number;
          closeButton?: boolean;
          closeOnClick?: boolean;
          className?: string;
        }) => {
          setLngLat: (c: [number, number]) => {
            setHTML: (html: string) => { addTo: (m: unknown) => { remove: () => void } };
          };
        };
      }).Popup;

      const openPopupFor = (club: ClubMapFaceliftItem): void => {
        if (popupRef.current) {
          popupRef.current.remove();
          popupRef.current = null;
        }
        const href = `${clubHrefBase}/${encodeURIComponent(club.slug)}`;
        const html =
          `<a href="${href}" class="brisa-pinmap-popup-link" data-event="club-pin-tooltip-clicked" data-club-slug="${escapeHTML(club.slug)}">` +
          `${escapeHTML(club.name)}` +
          '</a>';
        // closeOnClick: false — o click no marker propagava pro map e fechava o
        // popup recém-aberto (Bruno reportou popup invisível). Removemos pelo
        // stopPropagation + recriação manual a cada novo marker click.
        const popup = new PopupCtor({
          offset: 18,
          closeButton: false,
          closeOnClick: false,
          className: 'brisa-pinmap-popup',
        })
          .setLngLat([club.location_lng, club.location_lat])
          .setHTML(html)
          .addTo(map);
        popupRef.current = popup;
      };

      // Add markers novos
      clubs.forEach((club) => {
        if (markersRef.current.has(club.slug)) return;

        const el = document.createElement('button');
        el.type = 'button';
        el.className = 'brisa-pinmap-marker';
        el.setAttribute('data-state', club.slug === selectedClubSlug ? 'selected' : 'default');
        el.setAttribute('data-integration-status', club.integration_status);
        el.setAttribute('aria-label', `Ver ${club.name} no mapa`);
        el.innerHTML =
          '<span aria-hidden="true" class="ring-outer"></span>' +
          '<span aria-hidden="true" class="ring-middle"></span>' +
          '<span aria-hidden="true" class="dot-inner"></span>';

        const handleClick = (): void => {
          openPopupFor(club);
          onMarkerClick?.(club.slug, club.integration_status);
        };
        el.addEventListener('click', (e: MouseEvent) => {
          // stopPropagation evita que o click bubble pra o canvas do Mapbox,
          // que dispara handlers internos de "clicked on map" e fecha o popup.
          e.stopPropagation();
          handleClick();
        });
        el.addEventListener('keydown', (e: KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
          }
        });

        const marker = new mapboxgl.Marker({ element: el })
          .setLngLat([club.location_lng, club.location_lat])
          .addTo(map);

        markersRef.current.set(club.slug, { element: el, marker });
      });
    }, [clubs, isLoaded, selectedClubSlug, onMarkerClick, clubHrefBase]);

    // Sync selected state — atualiza só data-state sem recriar markers
    React.useEffect(() => {
      markersRef.current.forEach(({ element }, slug) => {
        element.setAttribute('data-state', slug === selectedClubSlug ? 'selected' : 'default');
      });
    }, [selectedClubSlug]);

    // Expor fitBounds imperativo
    React.useImperativeHandle(
      ref,
      () => ({
        fitBoundsToClubs: (clubsToFit) => {
          const map = mapRef.current as {
            fitBounds: (b: unknown, opts: unknown) => void;
          } | null;
          const mapboxgl = mapboxModRef.current as {
            LngLatBounds: new () => { extend: (c: [number, number]) => void };
          } | null;
          if (!map || !mapboxgl || clubsToFit.length === 0) return;

          const bounds = new mapboxgl.LngLatBounds();
          clubsToFit.forEach((c) => bounds.extend([c.location_lng, c.location_lat]));
          map.fitBounds(bounds, { padding: 60, maxZoom: 14, duration: 800 });
        },
      }),
      [],
    );

    if (error === 'no_token') {
      return (
        <div
          role="alert"
          className={className}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'hsl(var(--text-08))',
            color: 'hsl(var(--text-56))',
            borderRadius: 24,
            padding: 48,
            textAlign: 'center',
            fontSize: '0.875rem',
          }}
        >
          {locale === 'pt'
            ? 'Mapa indisponível no momento.'
            : 'Mapa no disponible en este momento.'}
        </div>
      );
    }

    return (
      <div
        ref={containerRef}
        role="region"
        aria-label={locale === 'pt' ? 'Mapa de clubes em Barcelona' : 'Mapa de clubes en Barcelona'}
        className={className}
        style={{ width: '100%', height: '100%', borderRadius: 24, overflow: 'hidden' }}
      />
    );
  },
);
